/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { isArray } from '@ember/array';
import { isEmpty } from '@ember/utils';
import { get } from '@ember/object';
import { service } from '@ember/service';

import ContactType from 'linced-app/enums/contact-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { findBy, sortBy } from '@linc-technologies/linced-common/utils/arrays';
import { hash } from '@linc-technologies/linced-common/utils/promise';

export default class LinkSiblingsRoute extends Route {
  // Services
  @service
  session;

  @service
  store;

  model() {
    return hash({
      learners: this.store.query('person', {
        type: PersonType.LEARNER,
        includeFormer: true,
      }),
      caregivers: this.store.query('person', { type: PersonType.CAREGIVER }),
    });
  }

  afterModel(model) {
    let potential = [];
    let jurisdiction =
      this.get('session.school.jurisdiction') ?? Jurisdiction.NZ_MOE;

    model.caregivers.forEach((caregiver) => {
      let learnerIds = caregiver.get('caregiver.learnerIds');
      if (isArray(learnerIds) && learnerIds.length > 1) {
        // Find caregiver's first learner who is associated with this school.
        let first = null;
        let firstRelationship = 0;
        let idx = 0;
        while (first === null && idx < learnerIds.length) {
          first = this.store.peekRecord('person', learnerIds.objectAt(idx++));
          if (!isEmpty(first)) {
            let contact = findBy(
              first.learner.contacts,
              'caregiverId',
              caregiver.id,
            );
            if (isEmpty(contact) || contact.type !== ContactType.PRIMARY) {
              // Not a primary contact.  Skip.
              first = null;
            } else {
              firstRelationship = contact.relationship;
            }
          }
        }
        if (first === null) {
          // This caregiver is not a primary contact for any learners at this school.
          return;
        }

        let existing = first.get('learner.siblings') ?? [];
        let temp = findBy(potential, 'personId', first.id);
        let newSiblings = [];
        let isNew = true;
        if (!isEmpty(temp)) {
          isNew = false;
          newSiblings = get(temp, 'siblings');
        }
        let firstAddress1 = '';
        // TODO: Handle other jurisdictions if/when they get student address fields
        if (jurisdiction === Jurisdiction.NZ_MOE) {
          firstAddress1 = (first.get('fields.nzMOE:address1.value') ?? '')
            .trim()
            .toLowerCase();
        }
        learnerIds.forEach((id) => {
          if (id !== first.id) {
            if (!findBy(existing, 'personId', id)) {
              if (findBy(newSiblings, 'id', id)) {
                // Already mapped (via a different caregiver)
                return;
              }
              let inverse = findBy(potential, 'personId', id);
              if (
                !isEmpty(inverse) &&
                findBy(inverse.siblings, 'id', first.id)
              ) {
                // Already mapped (via the potential sibling)
                return;
              }

              let person = this.store.peekRecord('person', id);
              if (isEmpty(person)) {
                // Potential sibling not found in this school.
                return;
              }
              let contact = findBy(
                person.learner.contacts,
                'caregiverId',
                caregiver.id,
              );
              if (isEmpty(contact) || contact.type !== ContactType.PRIMARY) {
                // Not a primary contact.  Skip.
                return;
              }

              if (contact.relationship !== firstRelationship) {
                // Relationship to learner is different.  Skip.
                return;
              }

              // TODO: Handle other jurisdictions if/when they get student address fields
              if (jurisdiction === Jurisdiction.NZ_MOE) {
                let address1 = (person.get('fields.nzMOE:address1.value') ?? '')
                  .trim()
                  .toLowerCase();
                if (address1 !== firstAddress1) {
                  // Address 1 doesn't match.  Skip.
                  return;
                }
              }

              newSiblings.push({
                id,
                person,
                firstName: person.firstName,
                lastName: person.lastName,
                contact,
              });
            }
          }
        });
        if (isNew && newSiblings.length > 0) {
          potential.push({
            personId: first.id,
            person: first,
            siblings: newSiblings,
          });
        }
      }
    });

    model.potential = sortBy(potential, 'person.lastName', 'person.firstName');
    model.refresh = () => this.refresh();
  }
}
